<template>
<standard-dialog v-model="visible" scrollable persistent>

  <template slot="title">
    <span class="title">{{ $t('kanban.edit')}}</span>
  </template>

  <name-text-field v-model="nameText" :required="false" autofocus></name-text-field>

  <v-select box v-model="selectedType" :items="types" return-object :label="$t('kanban.types')" @change="onTypeChanged">
    <template slot="item" slot-scope="data">
      <v-list-tile-avatar>
        <img :src="data.item.icon"></img>
      </v-list-tile-avatar>
      <v-list-tile-content>
        <v-list-tile-title>{{ data.item.text }}</v-list-tile-title>
      </v-list-tile-content>
    </template>
  </v-select>

  <v-select :disabled="!isShownChart" box v-model="selectedChart" :items="filteredCharts" :label="$t('kanban.charts')" @change="onChartChanged">
    <template slot="item" slot-scope="data">
      <v-list-tile-avatar>
        <v-icon large>{{data.item.icon}}</v-icon>
      </v-list-tile-avatar>
      <v-list-tile-content>
        <v-list-tile-title>{{ data.item.text }}</v-list-tile-title>
      </v-list-tile-content>
    </template>
  </v-select>

  <v-select v-if="isShownUnit" box v-model="selectedUnit" :items="(selectedType||{}).unit" :label="$t('kanban.unit')">
    <template slot="item" slot-scope="data">
      <v-list-tile-avatar>
        <img :src="data.item.icon"></img>
      </v-list-tile-avatar>
      <v-list-tile-content>
        <v-list-tile-title>{{ data.item.text }}</v-list-tile-title>
      </v-list-tile-content>
    </template>
  </v-select>

  <device-selector :disabled="!isShownDevice" box multiple v-model="selectedDevices" :label="$t('kanban.devices')" :functypes="(selectedType||{}).functypes" clearable></device-selector>

  <v-select v-if="isShownDays" box v-model="selectedDays" :items="days" :label="$t('kanban.days')">
    <template slot="item" slot-scope="data">
      <v-list-tile-content>
        <v-list-tile-title>{{ data.item.text }}</v-list-tile-title>
      </v-list-tile-content>
    </template>
  </v-select>

  <template slot="actions">
    <v-spacer></v-spacer>
    <v-btn :disabled="!isShownSaveButton" :loading="saving" flat color="primary" @click="save">{{ $t('kanban.save') }}</v-btn>
  </template>

</standard-dialog>
</template>

<script>
import UserAuth from '@/components/auth/UserAuth'
import Dialog from '@/components/utils/Dialog'
import DeviceSelector from '@/components/device/DeviceSelector'
import NameTextField from '@/components/utils/NameTextField'

export default {
  name: 'KanbanEditorDialog',
  mixins: [UserAuth],
  props: {
    value: {
      type: Boolean,
      default: false
    },
    kanban: {
      type: Object
    }
  },
  components: {
    'standard-dialog': Dialog,
    'device-selector': DeviceSelector,
    'name-text-field': NameTextField
  },
  watch: {
    selectedDevices(val) {
      if (val.length > 3) {
        this.$emit('toast', this.$t('kanban.selection_limit_reached'));
      }
      while (val.length > 3) {
        val.splice(val.length - 1, 1);
      }
    }
  },
  data() {
    return {
      types: [{
        eventCode: 4801,
        text: this.$t('kanban.temperature'),
        value: 'temperature',
        icon: this.$FuncTypeIcons[11],
        functypes: [11],
        charts: ['trend'],
        unit: [{
          value: 1,
          text: this.$t('kanban.celsius'),
          icon: this.$FuncTypeIcons[11]
        }, {
          value: 2,
          text: this.$t('kanban.fahrenheit'),
          icon: this.$FuncTypeIcons[11]
        }]
      }, {
        eventCode: 4802,
        text: this.$t('kanban.illumination'),
        value: 'illumination',
        icon: this.$FuncTypeIcons[12],
        functypes: [12],
        charts: ['trend']
      }, {
        eventCode: 4803,
        text: this.$t('kanban.humidity'),
        value: 'humidity',
        icon: this.$FuncTypeIcons[15],
        functypes: [15],
        charts: ['trend']
      }, {
        eventCode: 4804,
        text: this.$t('kanban.meter'),
        value: 'meter',
        icon: this.$FuncTypeIcons[29],
        functypes: [22, 26, 29],
        charts: ['trend', 'consumption']
      }, {
        eventCode: 4101,
        text: this.$t('kanban.pir'),
        value: 'pir',
        icon: this.$FuncTypeIcons[14],
        functypes: [14],
        charts: ['histogram']
      }, {
        eventCode: [5002, 5102],
        text: this.$t('kanban.level'),
        value: 'level',
        icon: this.$FuncTypeIcons[24],
        functypes: [24, 30, 41],
        charts: ['stepped']
      }, {
        eventCode: [5002, 5102],
        text: this.$t('kanban.onoff'),
        value: 'onoff',
        icon: this.$FuncTypeIcons[23],
        functypes: [22, 23, 36],
        charts: ['stepped']
      }, {
        eventCode: [4102, 4103],
        text: this.$t('kanban.door'),
        value: 'door',
        icon: this.$FuncTypeIcons[13],
        functypes: [13],
        charts: ['stepped']
      }],
      selectedType: null,
      charts: [{
        value: 'trend',
        text: this.$t('kanban.trend'),
        icon: 'show_chart',
        limit: 3
      }, {
        value: 'consumption',
        text: this.$t('kanban.consumption'),
        icon: 'opacity',
        limit: 3
      }, {
        value: 'histogram',
        text: this.$t('kanban.histogram'),
        icon: 'bar_chart',
        limit: 3
      }, {
        value: 'stepped',
        text: this.$t('kanban.trend'),
        icon: 'show_chart',
        limit: 3
      }],
      nameText: null,
      selectedChart: null,
      selectedUnit: null,
      selectedDevices: [],
      selectedDays: 1,
      days: [{
        text: '1',
        value: 1
      }, {
        text: '2',
        value: 2
      }, {
        text: '3',
        value: 3
      }, {
        text: '4',
        value: 4
      }, {
        text: '5',
        value: 5
      }, {
        text: '6',
        value: 6
      }, {
        text: '7',
        value: 7
      }],
      saving: false
    }
  },
  computed: {
    visible: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      }
    },
    kanbanId() {
      return (this.kanban || {}).key;
    },
    isShownChart() {
      return this.selectedType;
    },
    isShownUnit() {
      return this.selectedChart && this.selectedType && this.selectedType.unit;
    },
    isShownDevice() {
      return ((this.selectedChart && !(this.selectedType || {}).unit) || this.selectedUnit);
    },
    isShownDays() {
      return this.selectedDevices.length > 0;
    },
    isShownSaveButton() {
      return this.selectedDevices.length > 0;
    },
    filteredCharts() {
      if (this.selectedType) {
        if (this.selectedType.charts)
          return this.charts.filter(c => this.selectedType.charts.includes(c.value));
        else
          return this.charts;
      } else {
        return [];
      }
    }
  },
  methods: {
    triggerFilter(devices) {
      if (this.selectedType) {
        if (this.selectedType.battery)
          return devices.filter(d => d.battery < 255);
        else if (this.selectedType.functypes)
          return devices.filter(d => this.selectedType.functypes.includes(d.functype));
        else
          return devices;
      } else {
        return [];
      }
    },
    onTypeChanged() {
      this.selectedChart = null;
      this.selectedUnit = null;
      this.selectedDevices = [];
      if (this.filteredCharts.length == 1)
        this.selectedChart = this.filteredCharts[0].value;
    },
    onChartChanged() {
      this.selectedUnit = null;
      this.selectedDevices = [];
      switch (this.selectedChart) {
        case 'trend':
          if (this.selectedType.eventCode === 4804) this.selectedUnit = 5;
          break;
        case 'consumption':
          this.selectedUnit = 9;
          break;
        case 'stepped':
          this.selectedUnit = 3;
          break;
      }
    },
    save() {
      let r = {
        name: this.nameText || '',
        type: this.selectedType.value,
        eventCode: this.selectedType.eventCode,
        chart: this.selectedChart,
        devices: this.selectedDevices,
        days: parseInt(this.selectedDays) || 1,
        updatedTime: new Date().getTime(),
        data: false,
        unit: this.selectedUnit || null
      };

      this.saving = true;
      let ref = this.$db.ref('userKanban').child(this.userId);
      if (this.kanbanId) {
        ref.child(this.kanbanId).update(r)
          .then(() => this.$emit('close', this.kanbanId))
          .catch(err => {
            this.saving = false;
            this.$emit('toast', err.message);
          });
      } else {
        r.createdTime = r.updatedTime;
        let refNewKanban = ref.push();
        refNewKanban.set(r)
          .then(() => this.$emit('close', refNewKanban.key))
          .catch(err => {
            this.saving = false;
            this.$emit('toast', err.message);
          });
      }
    }
  },
  mounted() {
    if (this.kanban) {
      this.nameText = this.kanban.name;
      this.selectedType = this.types.find(t => t.value == this.kanban.type);
      this.selectedChart = this.kanban.chart;
      this.selectedUnit = this.kanban.unit;
      this.selectedDevices = this.kanban.devices || [];
      this.selectedDays = this.kanban.days || 1;
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/juice/KanbanEditorDialog.vue